import { StatusCode } from '@/modules/asset/constants';
import { computed, Ref } from '@vue/composition-api';
import { match } from 'ts-pattern';

export function useAssetStatus(status: Ref<StatusCode | undefined>) {
    const label = computed(() => {
        switch (status.value) {
            case StatusCode.Available:
                return 'Available';
            case StatusCode.Internal:
                return 'Pending Publication';
            case StatusCode.Pending:
                return 'Pending Approval';
            default:
                return status.value;
        }
    });

    const statusClass = {
        [StatusCode.Incomplete]: 'text-white bg-purple-600',
        [StatusCode.Uploading]: 'text-white bg-teal-600',
        [StatusCode.Updating]: 'text-white bg-orange-800',
        [StatusCode.Deleted]: 'text-white bg-cherry-900',
        [StatusCode.Available]: 'text-white bg-green-800',
        [StatusCode.Internal]: 'text-green-700 bg-green-100',
        [StatusCode.Pending]: 'text-green-700 bg-green-100',
        [StatusCode.Archived]: 'text-white bg-slate-500', // for model assets
    };

    const colour = computed(() => (status.value ? statusClass[status.value] : 'text-yellow-700 bg-yellow-100'));

    const icon = computed(() =>
        match<StatusCode | undefined, string>(status.value)
            .with(StatusCode.Draft, StatusCode.Incomplete, () => 'solar:menu-dots-circle-bold')
            .with(StatusCode.Uploading, () => 'solar:cloud-upload-bold')
            .with(StatusCode.Updating, () => 'solar:server-square-update-bold')
            .with(StatusCode.Deleted, () => 'solar:trash-bin-minimalistic-bold')
            .with(StatusCode.Available, () => 'solar:check-circle-bold')
            .with(StatusCode.Internal, () => 'solar:check-circle-bold')
            .with(StatusCode.Pending, () => 'solar:check-circle-bold')
            .with(StatusCode.Archived, () => 'solar:archive-bold')
            .with(undefined, () => 'solar:text-underline-circle-bold')
            .exhaustive(),
    );

    const iconColour = computed(() => {
        switch (status.value) {
            case StatusCode.Incomplete:
            case StatusCode.Draft:
                return 'text-purple-600';
            case StatusCode.Uploading:
                return 'text-teal-700';
            case StatusCode.Updating:
                return 'text-orange-700';
            case StatusCode.Deleted:
                return 'text-cherry-700';
            case StatusCode.Available:
            case StatusCode.Internal:
            case StatusCode.Pending:
            default:
                return 'text-green-700';
        }
    });

    const tooltip = computed(() => {
        switch (status.value) {
            case StatusCode.Internal:
                return 'The asset can be viewed by organisation members only; the process to ensure the consent of input data asset provider(s), for publication in the Data Marketplace has not started yet.';
            case StatusCode.Pending:
                return 'The process for publishing in the Data Marketplace has started yet some derivation contracts have not been yet finalized.';
            default:
                return null;
        }
    });

    return { label, colour, icon, iconColour, statusClass, tooltip };
}
